import type { GameState } from '../types';

// Save format version for future migrations
const CURRENT_SAVE_VERSION = 1;

interface SaveData {
    version: number;        // Schema version
    timestamp: number;      // When save was created
    gameState: GameState;   // Only serializable game state
}

interface StoreState extends GameState {
    // Action methods are not part of GameState
    [key: string]: any;
}

/**
 * Export game state to a JSON string
 * Uses whitelist approach to explicitly extract only game state fields
 */
export const exportSaveData = (state: StoreState): string => {
    // Explicitly extract only GameState fields (no methods)
    const gameState: GameState = {
        resources: state.resources,
        skills: state.skills,
        market: state.market,
        marketSlots: state.marketSlots,
        tradeHistory: state.tradeHistory,
        farming: {
            plots: state.farming.plots,
            rainTotem: state.farming.rainTotem,
            mutator: state.farming.mutator,
            gnomeHuts: state.farming.gnomeHuts,
        },
        crafting: {
            slots: state.crafting.slots,
        },
        drones: state.drones,
        upgrades: state.upgrades,
        miningReadyAt: state.miningReadyAt,
        lastListingItem: state.lastListingItem,
        lastListingType: state.lastListingType,
        lastSaveTime: Date.now(), // Update to current time
    };

    const saveData: SaveData = {
        version: CURRENT_SAVE_VERSION,
        timestamp: Date.now(),
        gameState,
    };

    return JSON.stringify(saveData, null, 2);
};

/**
 * Import game state from a JSON string
 * Returns null if validation fails
 */
export const importSaveData = (saveString: string): GameState | null => {
    try {
        // 1. Parse JSON
        const parsed = JSON.parse(saveString);

        // 2. Validate structure
        if (!validateSaveData(parsed)) {
            console.error('Invalid save data structure');
            return null;
        }

        // 3. Check version compatibility - reject saves from future versions
        if (parsed.version > CURRENT_SAVE_VERSION) {
            console.error('Save file is from a newer version of the game');
            return null;
        }

        // 4. Run migrations if needed
        let migratedData = parsed;
        if (parsed.version < CURRENT_SAVE_VERSION) {
            migratedData = migrateSaveData(parsed);
        }

        // 5. Validate game state fields
        if (!validateGameState(migratedData.gameState)) {
            console.error('Invalid game state data');
            return null;
        }

        return migratedData.gameState;
    } catch (error) {
        console.error('Failed to import save:', error);
        return null;
    }
};

/**
 * Validate SaveData structure
 */
const validateSaveData = (data: unknown): data is SaveData => {
    if (typeof data !== 'object' || data === null) return false;

    const d = data as any;

    // Check required fields
    if (typeof d.version !== 'number') return false;
    if (typeof d.timestamp !== 'number') return false;
    if (typeof d.gameState !== 'object' || d.gameState === null) return false;

    return true;
};

/**
 * Validate GameState has all required fields
 */
const validateGameState = (state: unknown): state is GameState => {
    if (typeof state !== 'object' || state === null) return false;

    const s = state as any;

    // Check all required top-level fields exist
    if (typeof s.resources !== 'object' || s.resources === null) return false;
    if (typeof s.skills !== 'object' || s.skills === null) return false;
    if (typeof s.market !== 'object' || s.market === null) return false;
    if (!Array.isArray(s.marketSlots)) return false;
    if (!Array.isArray(s.tradeHistory)) return false;
    if (typeof s.farming !== 'object' || s.farming === null) return false;
    if (!Array.isArray(s.farming.plots)) return false;
    if (typeof s.crafting !== 'object' || s.crafting === null) return false;
    if (!Array.isArray(s.crafting.slots)) return false;
    if (typeof s.drones !== 'object' || s.drones === null) return false;
    if (typeof s.upgrades !== 'object' || s.upgrades === null) return false;
    if (typeof s.miningReadyAt !== 'number') return false;
    if (typeof s.lastSaveTime !== 'number') return false;

    // Optional fields - validate type if present
    if (s.lastListingItem !== undefined && typeof s.lastListingItem !== 'string') return false;
    if (s.lastListingType !== undefined && s.lastListingType !== 'buy' && s.lastListingType !== 'sell') return false;

    // Validate nested structures
    if (s.farming.rainTotem !== undefined) {
        if (typeof s.farming.rainTotem !== 'object' || s.farming.rainTotem === null) return false;
        if (typeof s.farming.rainTotem.unlocked !== 'boolean') return false;
        if (typeof s.farming.rainTotem.lastCast !== 'number') return false;
        if (typeof s.farming.rainTotem.cooldown !== 'number') return false;
    }

    if (s.farming.mutator !== undefined) {
        if (typeof s.farming.mutator !== 'object' || s.farming.mutator === null) return false;
        if (typeof s.farming.mutator.unlocked !== 'boolean') return false;
    }

    if (s.farming.gnomeHuts !== undefined) {
        if (typeof s.farming.gnomeHuts !== 'object' || s.farming.gnomeHuts === null) return false;
        if (typeof s.farming.gnomeHuts.unlocked !== 'number') return false;
    }

    // Validate drones structure
    if (!Array.isArray(s.drones.unlockedTiers)) return false;
    if (!Array.isArray(s.drones.activeDrones)) return false;
    if (typeof s.drones.maxDrones !== 'number') return false;

    return true;
};

/**
 * Migrate save data from older versions to current version
 */
const migrateSaveData = (data: SaveData): SaveData => {
    let currentData = { ...data };

    // Migration from v0 to v1 (if we had a v0)
    // Example: if (currentData.version === 0) { ... }

    // Future migrations will be added here as the game evolves
    // Each migration should handle one version increment

    return currentData;
};

// --- Hard Reset Utilities ---

const HARD_RESET_FLAG = 'idlescape-force-reset';

/**
 * clearAllStorage
 * Clears localStorage, sessionStorage, and cookies.
 * Optionally preserves the hard reset flag in sessionStorage.
 */
const clearAllStorage = (preserveResetFlag = false) => {
    let flagValue: string | null = null;
    if (preserveResetFlag) {
        flagValue = sessionStorage.getItem(HARD_RESET_FLAG);
    }

    localStorage.clear();
    sessionStorage.clear();

    if (preserveResetFlag && flagValue) {
        sessionStorage.setItem(HARD_RESET_FLAG, flagValue);
    }

    // Clear cookies
    if (typeof document !== 'undefined' && document.cookie) {
        document.cookie.split(';').forEach(cookie => {
            const name = cookie.split('=')[0].trim();
            if (name) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            }
        });
    }
};

/**
 * triggerHardReset
 * Initiates the hard reset process: sets flag, clears storage, and reloads.
 */
export const triggerHardReset = () => {
    sessionStorage.setItem(HARD_RESET_FLAG, 'true');
    clearAllStorage(true);
    // Reload with cache-busting param
    window.location.replace(window.location.pathname + '?reset=' + Date.now());
};

/**
 * checkAndFinalizeHardReset
 * Checks for the hard reset flag on boot. If found, cleans up and reloads to origin.
 * Returns true if a reset is in progress (caller should probably stop rendering).
 */
export const checkAndFinalizeHardReset = (): boolean => {
    if (sessionStorage.getItem(HARD_RESET_FLAG) === 'true') {
        // Flag found! We are in the middle of a reset.
        // Clear everything completely (including the flag this time)
        clearAllStorage(false);
        // Reload to clean origin (removes query params)
        window.location.replace(window.location.origin);
        return true;
    }
    return false;
};

